/**
 * <HeroScrollCta /> — زرّ/سهم سفليّ يظهر تحت أيّ هيرو (الرئيسيّة + الصفحات الداخليّة).
 *
 * السلوك:
 *   - لو الأدمن ضبط label + href لهذه الصفحة → زرّ بالنصّ يفتح الرابط (داخليّ/خارجيّ).
 *   - لو لم يضبط شيئاً → سهم متحرّك يمرّر شاشة كاملة لأسفل (scrollBy 100vh).
 *   - لو الأدمن عطّل الـCTA لهذه الصفحة → لا يظهر شيء.
 */
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";
import {
  getPageHeroCtas,
  type PageHeroCta,
} from "@/utils/pageHeroCtas.functions";
import type { PageKey } from "@/utils/pageBackgrounds.shared";
import type { HeroCampaignText } from "@/hooks/useHeroVideoUrls";
import { softCtaOnMediaClass } from "@/components/ui/soft-cta";

export function HeroScrollCta({
  pageKey,
  campaignOverride,
}: {
  pageKey: PageKey | string;
  campaignOverride?: HeroCampaignText | null;
}) {
  const { locale } = useLocale();
  const getFn = useServerFn(getPageHeroCtas);
  const { data } = useQuery({
    queryKey: ["page-hero-ctas"],
    queryFn: () => getFn(),
    staleTime: 5 * 60_000,
  });

  const cta: PageHeroCta = (data?.map as Record<string, PageHeroCta> | undefined)?.[pageKey] ?? {
    enabled: true,
    labelAr: "",
    labelEn: "",
    href: "",
  };

  const hasOverride = Boolean(
    campaignOverride &&
      (campaignOverride.scroll_cta_label_ar ||
        campaignOverride.scroll_cta_label_en ||
        campaignOverride.scroll_cta_href ||
        campaignOverride.scroll_cta_enabled !== null),
  );
  const effective = hasOverride
    ? {
        enabled: campaignOverride?.scroll_cta_enabled ?? true,
        labelAr: campaignOverride?.scroll_cta_label_ar ?? "",
        labelEn: campaignOverride?.scroll_cta_label_en ?? "",
        href: campaignOverride?.scroll_cta_href ?? "",
      }
    : cta;
  if (!effective.enabled) return null;

  const label = (locale === "ar" ? effective.labelAr : effective.labelEn).trim();
  const href = effective.href.trim();
  const hasLink = label.length > 0 && href.length > 0;

  if (hasLink) {
    const isExternal = /^https?:\/\//i.test(href) || href.startsWith("mailto:") || href.startsWith("tel:");
    const className =
      softCtaOnMediaClass;
    return (
      <div className="absolute inset-x-0 bottom-44 z-20 flex justify-center px-4 md:bottom-28">
        {isExternal ? (
          <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer noopener" className={className}>
            <span>{label}</span>
            <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
          </a>
        ) : (
          <Link to={href} className={className}>
            <span>{label}</span>
            <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
          </Link>
        )}
      </div>
    );
  }

  // وضع السهم الافتراضيّ — يمرّر شاشة كاملة لأسفل
  return (
    <button
      type="button"
      onClick={() => window.scrollBy({ top: window.innerHeight, behavior: "smooth" })}
      aria-label={locale === "ar" ? "نزول" : "Scroll down"}
      className="absolute bottom-44 left-1/2 z-20 -translate-x-1/2 flex h-12 w-12 items-center justify-center rounded-full border border-white/30 bg-white/10 text-white backdrop-blur-md transition-transform hover:scale-110 md:bottom-28"
    >
      <ChevronDown className="h-5 w-5 animate-bounce" />
    </button>
  );
}
